import { useCallback, useEffect, useState } from 'react';

import * as passkeyApi from 'darwin_core/api/passkeys';

import ModalShell from '../components/ModalShell.jsx';
import ScreenHeader from '../components/ScreenHeader.jsx';
import { ACCOUNT_PATH } from '../navigation/paths.js';
import {
  addPasskey,
  MAX_PASSKEYS,
  passkeyAlreadyAdded,
  passkeyCancelled,
  passkeysSupported,
} from '../passkeys/webauthn.js';

// The app's Account > Passkeys screen (app/src/screens/Account/Settings/
// Passkeys.js) as a web page: the account's passkeys, each with when it was
// added and last used and a way to remove it, plus adding one in this browser.
// The copy follows the app's, with "this browser" where it says "this phone".

const formatDate = (timestamp) =>
  new Date(timestamp).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const describeUse = (passkey) =>
  [
    `Added ${formatDate(passkey.CreatedAt)}`,
    passkey.LastUsedAt ? `last used ${formatDate(passkey.LastUsedAt)}` : null,
  ]
    .filter((part) => !!part)
    .join(', ');

const LOAD_FAILED =
  'Your passkeys could not be loaded. Please try again later.';
const REMOVE_FAILED =
  'The passkey could not be removed. Please try again later.';

// What to say when adding a passkey fails — the app's addPasskeyErrorMessage. A
// cancelled prompt is not a failure and never reaches here.
function addFailedMessage(error) {
  if (error?.code === 'PASSKEY_LIMIT_REACHED') {
    return `You can have up to ${MAX_PASSKEYS} passkeys. Remove one you no longer use, then try again.`;
  }
  if (passkeyAlreadyAdded(error)) {
    return 'This browser already has a passkey for your Halo account.';
  }
  return 'A passkey could not be added. Please try again later.';
}

export default function PasskeysScreen() {
  const [supported] = useState(passkeysSupported);
  // null until the first load finishes
  const [passkeys, setPasskeys] = useState(null);
  // The latest load failed, so the list on hand can't be trusted: show the error
  // alone rather than an empty list (or one a removal has just changed).
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toRemove, setToRemove] = useState(null);

  const load = useCallback(async () => {
    try {
      setPasskeys(await passkeyApi.listPasskeys());
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
      setError(LOAD_FAILED);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd() {
    setError(null);
    setBusy(true);
    try {
      await addPasskey();
      await load();
    } catch (ex) {
      if (!passkeyCancelled(ex)) {
        setError(addFailedMessage(ex));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    const passkey = toRemove;
    setToRemove(null);
    setError(null);
    setBusy(true);
    try {
      await passkeyApi.deletePasskey(passkey.id);
      await load();
    } catch {
      setError(REMOVE_FAILED);
    } finally {
      setBusy(false);
    }
  }

  const atLimit = (passkeys?.length ?? 0) >= MAX_PASSKEYS;

  return (
    <main className="app-shell screen passkeys">
      <ScreenHeader
        title="Passkeys"
        sub="Sign in without your password"
        backTo={ACCOUNT_PATH}
        backLabel="Account"
      />
      <p className="passkeys__note">
        Use your fingerprint, face or device PIN to sign in to Halo instead of
        typing your password.
      </p>
      {supported ? null : (
        <p className="passkeys__note">
          Passkeys aren’t supported in this browser.
        </p>
      )}

      {/* Always present so assistive tech has the live region before an error
          lands; collapses while empty. */}
      <p className="form-error" role="alert" aria-live="assertive">
        {error}
      </p>

      {loadFailed ? null : passkeys === null ? (
        <p className="passkeys__note">Loading your passkeys…</p>
      ) : passkeys.length === 0 ? (
        <p className="passkeys__note">You haven’t added a passkey yet.</p>
      ) : (
        <ul className="passkeys__list" aria-label="Your passkeys">
          {passkeys.map((passkey) => (
            <li key={passkey.id} className="passkeys__row">
              <div className="passkeys__row-text">
                <span className="passkeys__name">{passkey.DeviceName}</span>
                <span className="passkeys__use">{describeUse(passkey)}</span>
              </div>
              <button
                type="button"
                className="link-button"
                aria-label={`Remove passkey ${passkey.DeviceName}`}
                disabled={busy}
                onClick={() => setToRemove(passkey)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {supported && passkeys !== null && !loadFailed ? (
        atLimit ? (
          <p className="passkeys__note">
            You have {MAX_PASSKEYS} passkeys, the most an account can have.
            Remove one you no longer use to add another.
          </p>
        ) : (
          <button
            type="button"
            className="passkeys__add"
            disabled={busy}
            onClick={handleAdd}
          >
            {busy ? 'Working…' : 'Add a passkey'}
          </button>
        )
      ) : null}

      {toRemove ? (
        <ModalShell
          labelledBy="passkey-remove-title"
          className="schedule-modal--confirm"
          onClose={() => setToRemove(null)}
        >
          <h2 id="passkey-remove-title" className="schedule-modal__title">
            Remove passkey?
          </h2>
          <p className="schedule-modal__desc">
            “{toRemove.DeviceName}” will no longer be able to sign in to your
            Halo account.
          </p>
          <div className="schedule-modal__actions">
            <button
              type="button"
              className="schedule-modal__cancel"
              onClick={() => setToRemove(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={handleRemove}
            >
              Remove
            </button>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
}
